/**
 * Opportunities: brands' campaign open calls that fit this creator, best fit
 * first with a line on why, and "My applications" with where each one
 * stands. A campaign's page shows the brief and the apply form — the price
 * starts at the creator's closest package and the pitch can be drafted with
 * AI (docs/creators/SPEC.md, "Campaign open calls").
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useI18n, useT } from '../lib/i18n';
import {
  arr,
  big,
  cleanRupees,
  compactCount,
  kindLabel,
  microsToRupees,
  nicheLabel,
  languageLabel,
  parseLinks,
  platformLabel,
} from '../lib/creator';
import { formatINR, shortDate } from '../lib/format';
import {
  ActionBar,
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Meta,
  PageHero,
  SubHeader,
  Spinner,
  type Tone,
} from '../components/Primitives';
import { Icon } from '../components/Icon';
import { AiHelper, Chip } from '../components/Creator';

// ------------------------------------------------------------------ shapes

interface Opportunity {
  id: string;
  brandName: string;
  title: string;
  brief: string;
  productNote?: string | null;
  niches?: string[] | null;
  cities?: string[] | null;
  languages?: string[] | null;
  platforms?: string[] | null;
  deliverableKinds?: string[] | null;
  followersMin?: number | null;
  followersMax?: number | null;
  budgetMinMicros?: string | null;
  budgetMaxMicros?: string | null;
  creatorsWanted?: number | null;
  applyBy: string;
  dueOn?: string | null;
  barterAllowed?: boolean | null;
  acceptingApplications?: boolean;
  isNew?: boolean;
  match?: { why?: string | null; score?: number; misses?: string[]; matches?: boolean } | null;
  suggestedPriceMicros?: string | null;
}

interface Application {
  id: string;
  campaignId: string;
  campaignTitle: string;
  brandName: string;
  priceMicros: string;
  pitch: string;
  sampleLinks?: string[] | null;
  status: string;
  dealId?: string | null;
  unseen?: boolean;
  campaignOpen?: boolean;
  createdAt: string;
  decidedAt?: string | null;
}

const APP_STATUS: Record<string, { hi: string; en: string; tone: Tone }> = {
  applied: { hi: 'भेज दिया', en: 'Applied', tone: 'neutral' },
  shortlisted: { hi: 'शॉर्टलिस्ट', en: 'Shortlisted', tone: 'info' },
  accepted: { hi: 'चुने गए', en: 'Picked — deal made', tone: 'good' },
  rejected: { hi: 'इस बार नहीं', en: 'Not this time', tone: 'bad' },
  withdrawn: { hi: 'वापस लिया', en: 'Withdrawn', tone: 'neutral' },
};

const errText = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.message : fallback;

function budgetLine(o: Opportunity, T: (hi: string, en: string) => string): string {
  const lo = big(o.budgetMinMicros);
  const hi = big(o.budgetMaxMicros);
  if (lo > 0n && hi > 0n) return `${formatINR(lo)} – ${formatINR(hi)}`;
  if (hi > 0n) return T(`${formatINR(hi)} तक`, `Up to ${formatINR(hi)}`);
  if (lo > 0n) return T(`${formatINR(lo)} से`, `From ${formatINR(lo)}`);
  return T('बजट खुला है', 'Open budget');
}

// --------------------------------------------------------------- the feed

export function CreatorOpportunitiesPage() {
  const T = useT();
  const { lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'mine' ? 'mine' : 'feed';
  const [feed, setFeed] = useState<{
    campaigns: Opportunity[];
    profileReady: boolean;
    isPublic: boolean;
  } | null>(null);
  const [apps, setApps] = useState<Application[] | null>(null);
  const [updates, setUpdates] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      if (tab === 'feed') {
        const [r, c] = await Promise.all([
          api.get<{ campaigns?: Opportunity[]; profileReady?: boolean; isPublic?: boolean }>(
            '/v1/partner/creator/opportunities',
          ),
          api
            .get<{ updates?: number }>('/v1/partner/creator/opportunities/count')
            .catch(() => ({ updates: 0 })),
        ]);
        setFeed({
          campaigns: arr(r.campaigns),
          profileReady: r.profileReady !== false,
          isPublic: r.isPublic !== false,
        });
        setUpdates(c.updates ?? 0);
      } else {
        const r = await api.get<{ applications?: Application[] }>(
          '/v1/partner/creator/applications',
        );
        setApps(arr(r.applications));
        setUpdates(0);
      }
      setError(null);
    } catch (err) {
      setError(errText(err, T('कुछ गड़बड़ हुई।', 'Something went wrong.')));
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const withdraw = async (id: string) => {
    try {
      await api.post(`/v1/partner/creator/applications/${id}/withdraw`, {});
      await load();
    } catch (err) {
      setError(errText(err, T('वापस नहीं हुआ।', 'Could not withdraw.')));
    }
  };

  const campaigns = feed?.campaigns ?? [];
  return (
    <div className="space-y-4">
      <PageHero
        icon="sparkle"
        eyebrow={T('मौके', 'Opportunities')}
        title={
          campaigns.length > 0
            ? T(`${campaigns.length} कैंपेन आपके लिए`, `${campaigns.length} campaigns for you`)
            : T('ब्रांड के कैंपेन', 'Brand campaigns')
        }
        sub={T(
          'ब्रांड बताते हैं क्या चाहिए — अपना दाम और आइडिया भेजें। चुने जाने पर डील बनती है।',
          'Brands say what they need — send your price and idea. If they pick you, it becomes a deal.',
        )}
      />

      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {(
          [
            ['feed', T('मेरे लिए', 'For you')],
            ['mine', T('मेरी एप्लिकेशन', 'My applications')],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setParams(k === 'feed' ? {} : { tab: k }, { replace: true })}
            aria-pressed={tab === k}
            className={`inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-4 text-sm font-semibold transition ${
              tab === k ? 'text-white shadow-lift' : 'bg-panel text-muted shadow-card'
            }`}
            style={
              tab === k
                ? { backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }
                : undefined
            }
          >
            {label}
            {k === 'mine' && updates > 0 && (
              <span className="tnum rounded-full bg-rose-500 px-1.5 text-[11px] text-white">
                {updates}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && <ErrorNote message={error} onRetry={() => void load()} />}

      {tab === 'feed' ? (
        !feed ? (
          error ? null : (
            <Spinner />
          )
        ) : !feed.profileReady ? (
          <EmptyState
            title={T('पहले मीडिया किट बनाएँ', 'Set up your media kit first')}
            body={T(
              'कैंपेन आपकी निश, शहर, भाषा और चैनल से मिलाए जाते हैं।',
              'Campaigns are matched to your niches, city, languages and channels.',
            )}
            action={
              <Link to="/creator/kit" className="btn-primary">
                {T('मीडिया किट', 'Media kit')}
              </Link>
            }
          />
        ) : campaigns.length === 0 ? (
          <EmptyState
            title={T('अभी कोई कैंपेन नहीं', 'No campaigns for you right now')}
            body={T(
              'नए कैंपेन आते ही यहाँ दिखेंगे। ज़्यादा मौकों के लिए अपनी निश, भाषा और चैनल पूरे रखें।',
              'New ones show up here as brands post them. Keep your niches, languages and channels up to date to match more.',
            )}
          />
        ) : (
          <>
            {!feed.isPublic && (
              <Card className="text-sm">
                {T(
                  'अप्लाई करने के लिए अपनी मीडिया किट पब्लिक करें।',
                  'Make your media kit public to apply.',
                )}{' '}
                <Link to="/creator/kit" className="font-semibold underline">
                  {T('मीडिया किट', 'Media kit')}
                </Link>
              </Card>
            )}
            <ul className="space-y-2.5">
              {campaigns.map((o) => (
                <li key={o.id}>
                  <Link
                    to={`/opportunities/${o.id}`}
                    className="card block p-4 transition active:scale-[.99]"
                  >
                    <span className="flex items-start justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{o.title}</span>
                        <span className="block truncate text-xs text-muted">{o.brandName}</span>
                      </span>
                      {o.isNew && <Badge tone="info">{T('नया', 'New')}</Badge>}
                    </span>
                    {o.match?.why && (
                      <span
                        className="mt-2 flex items-start gap-1.5 text-[13px] font-medium"
                        style={{ color: 'var(--sec-ink)' }}
                      >
                        <Icon name="check" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {o.match.why}
                      </span>
                    )}
                    <span className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                      <Meta icon="rupee">{budgetLine(o, T)}</Meta>
                      <Meta icon="clock">
                        {T('आखिरी दिन', 'Apply by')} {shortDate(o.applyBy, lang)}
                      </Meta>
                      {arr(o.deliverableKinds).length > 0 && (
                        <Meta icon="tag">
                          {arr(o.deliverableKinds)
                            .map((k) => kindLabel(k, lang))
                            .join(', ')}
                        </Meta>
                      )}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )
      ) : !apps ? (
        error ? null : (
          <Spinner />
        )
      ) : apps.length === 0 ? (
        <EmptyState
          title={T('अभी कोई एप्लिकेशन नहीं', 'No applications yet')}
          body={T(
            'किसी कैंपेन पर अप्लाई करें — यहाँ दिखेगा कि ब्रांड ने क्या तय किया।',
            'Apply to a campaign and see here what the brand decided.',
          )}
        />
      ) : (
        <ul className="space-y-2.5">
          {apps.map((a) => {
            const st = APP_STATUS[a.status] ?? { hi: a.status, en: a.status, tone: 'neutral' };
            const canWithdraw = a.status === 'applied' || a.status === 'shortlisted';
            return (
              <li key={a.id}>
                <Card>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{a.campaignTitle}</p>
                      <p className="truncate text-xs text-muted">{a.brandName}</p>
                    </div>
                    <Badge tone={st.tone} pulse={a.unseen}>
                      {lang === 'hi' ? st.hi : st.en}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    <Meta icon="rupee">{formatINR(a.priceMicros)}</Meta>
                    <Meta icon="clock">{shortDate(a.createdAt.slice(0, 10), lang)}</Meta>
                    {canWithdraw && a.campaignOpen === false && (
                      <Meta icon="alert">
                        {T(
                          'आवेदन बंद, ब्रांड चुन रहा है',
                          'Closed to new applicants; brand is choosing',
                        )}
                      </Meta>
                    )}
                  </div>
                  {(a.dealId || canWithdraw) && (
                    <div className="mt-3 flex gap-2">
                      {a.dealId && (
                        <Link to={`/deals/${a.dealId}`} className="btn-primary !min-h-[42px]">
                          {T('डील खोलें', 'Open the deal')}
                        </Link>
                      )}
                      {canWithdraw && (
                        <button
                          type="button"
                          className="btn-secondary !min-h-[42px]"
                          onClick={() => void withdraw(a.id)}
                        >
                          {T('वापस लें', 'Withdraw')}
                        </button>
                      )}
                    </div>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------- one campaign + apply

export function CreatorOpportunityPage() {
  const T = useT();
  const { lang } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<{
    campaign: Opportunity;
    match: Opportunity['match'];
    suggestedPriceMicros: string | null;
    application: Application | null;
    samples: Array<{ url: string; caption?: string | null }>;
    isPublic: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [price, setPrice] = useState('');
  const [pitch, setPitch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [extra, setExtra] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .get<NonNullable<typeof data>>(`/v1/partner/creator/opportunities/${id}`)
      .then((r) => {
        setData({ ...r, samples: arr(r.samples) });
        setPrice(microsToRupees(r.suggestedPriceMicros));
        setPicked(
          arr(r.samples)
            .slice(0, 2)
            .map((s) => s.url),
        );
      })
      .catch((err: unknown) =>
        setError(errText(err, T('यह कैंपेन नहीं खुला।', 'This campaign did not load.'))),
      );
  }, [id]);

  if (error && !data)
    return (
      <div>
        <SubHeader title={T('कैंपेन', 'Campaign')} back="/opportunities" />
        <ErrorNote message={error} />
      </div>
    );
  if (!data) return <Spinner />;
  const o = data.campaign;
  const mine = data.application;
  const canApply =
    o.acceptingApplications !== false &&
    data.match?.matches !== false &&
    data.isPublic &&
    (!mine || mine.status === 'withdrawn');

  const submit = async () => {
    setFormError(null);
    const p = cleanRupees(price);
    if (!p || Number(p) < 100)
      return setFormError(T('दाम ₹100 या ज़्यादा रखें।', 'Enter a price of ₹100 or more.'));
    if (pitch.trim().length < 10)
      return setFormError(
        T(
          'ब्रांड को दो-तीन लाइन में बताएँ आप क्या बनाएँगे।',
          'Tell the brand in a few lines what you would make.',
        ),
      );
    setBusy(true);
    try {
      await api.post(`/v1/partner/creator/opportunities/${o.id}/apply`, {
        price: p,
        pitch: pitch.trim(),
        sampleLinks: [...picked, ...parseLinks(extra)].slice(0, 10),
      });
      navigate('/opportunities?tab=mine');
    } catch (err) {
      setFormError(errText(err, T('भेजा नहीं जा सका।', 'Could not send.')));
    } finally {
      setBusy(false);
    }
  };

  const facts: Array<[string, string]> = [
    [T('बजट (हर क्रिएटर)', 'Budget per creator'), budgetLine(o, T)],
    [T('आखिरी दिन', 'Apply by'), shortDate(o.applyBy, lang)],
    ...(o.dueOn
      ? ([[T('काम कब तक', 'Content due'), shortDate(o.dueOn, lang)]] as Array<[string, string]>)
      : []),
    [
      T('क्या चाहिए', 'Content'),
      arr(o.deliverableKinds)
        .map((k) => kindLabel(k, lang))
        .join(', ') || T('कुछ भी', 'Any'),
    ],
    [
      T('प्लेटफ़ॉर्म', 'Platforms'),
      arr(o.platforms)
        .map((p) => platformLabel(p, lang))
        .join(', ') || T('कोई भी', 'Any'),
    ],
    [T('शहर', 'Cities'), arr(o.cities).join(', ') || T('कहीं भी', 'Anywhere')],
    [
      T('निश', 'Niches'),
      arr(o.niches)
        .map((n) => nicheLabel(n, lang))
        .join(', ') || T('कोई भी', 'Any'),
    ],
    [
      T('भाषा', 'Languages'),
      arr(o.languages)
        .map((l) => languageLabel(l, lang))
        .join(', ') || T('कोई भी', 'Any'),
    ],
    ...(o.followersMin || o.followersMax
      ? ([
          [
            T('फ़ॉलोअर', 'Followers'),
            `${o.followersMin ? compactCount(o.followersMin) : '0'} – ${o.followersMax ? compactCount(o.followersMax) : '∞'}`,
          ],
        ] as Array<[string, string]>)
      : []),
  ];

  return (
    <div className="space-y-4">
      <SubHeader title={o.title} sub={o.brandName} back="/opportunities" icon="sparkle" />

      {data.match?.matches === false ? (
        <Card className="text-sm">
          {T('यह कैंपेन ढूँढ रहा है:', 'This campaign is looking for:')}{' '}
          <b>{arr(data.match.misses).join(', ')}</b>
        </Card>
      ) : data.match?.why ? (
        <Card className="flex items-start gap-2 text-sm font-medium">
          <Icon name="check" className="mt-0.5 h-4 w-4 shrink-0" />
          <span style={{ color: 'var(--sec-ink)' }}>{data.match.why}</span>
        </Card>
      ) : null}

      <Card>
        <p className="eyebrow mb-1 text-muted">{T('ब्रीफ़', 'The brief')}</p>
        <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{o.brief}</p>
        {o.productNote && (
          <p className="mt-3 rounded-xl bg-panel-2 px-3 py-2 text-sm">
            <b>{T('प्रोडक्ट / विज़िट:', 'Product / visit:')}</b> {o.productNote}
          </p>
        )}
        {o.barterAllowed && (
          <p className="mt-2 text-xs text-muted">
            {T(
              'प्रोडक्ट आप रख सकते हैं — उसके हिसाब से दाम कम रख सकते हैं।',
              'You keep the product, so you may quote a lower price.',
            )}
          </p>
        )}
        <dl className="mt-3 divide-y divide-line text-sm">
          {facts.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 py-2">
              <dt className="text-muted">{k}</dt>
              <dd className="text-right font-medium">{v}</dd>
            </div>
          ))}
        </dl>
      </Card>

      {mine && mine.status !== 'withdrawn' ? (
        <Card>
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold">{T('आपने अप्लाई किया है', 'You applied')}</p>
            <Badge tone={APP_STATUS[mine.status]?.tone ?? 'neutral'}>
              {lang === 'hi'
                ? (APP_STATUS[mine.status]?.hi ?? mine.status)
                : (APP_STATUS[mine.status]?.en ?? mine.status)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted">
            {formatINR(mine.priceMicros)} · {mine.pitch}
          </p>
          {mine.dealId && (
            <Link to={`/deals/${mine.dealId}`} className="btn-primary mt-3">
              {T('डील खोलें', 'Open the deal')}
            </Link>
          )}
        </Card>
      ) : canApply ? (
        <Card className="space-y-4">
          <p className="font-display text-[17px] font-bold">{T('अप्लाई करें', 'Apply')}</p>
          <Field
            label={T('आपका दाम (₹, GST के बिना)', 'Your price (₹, before GST)')}
            hint={
              data.suggestedPriceMicros
                ? T(
                    'आपके सबसे नज़दीकी पैकेज का दाम भरा है।',
                    'Filled from your closest package.',
                  )
                : undefined
            }
          >
            <input
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))}
              className="tnum !text-xl font-bold"
            />
          </Field>
          {Number(cleanRupees(price)) >= 100 && (
            <p className="-mt-2 text-xs text-muted">
              {T('Lulu की 10% फ़ीस के बाद आपको मिलेंगे लगभग', 'After Lulu’s 10% you get about')}{' '}
              <b className="tnum">
                ₹{Math.round(Number(cleanRupees(price)) * 0.9).toLocaleString('en-IN')}
              </b>{' '}
              {T('(TDS से पहले)', '(before TDS)')}
            </p>
          )}
          <Field label={T('ब्रांड के लिए पिच', 'Your pitch to the brand')}>
            <textarea
              rows={5}
              value={pitch}
              onChange={(e) => setPitch(e.target.value.slice(0, 2000))}
              placeholder={T(
                'आप क्या बनाएँगे, क्यों आप सही हैं, और कब तक।',
                'What you would make, why you fit, and by when.',
              )}
            />
          </Field>
          <AiHelper
            task="pitch"
            label={T('AI से पिच लिखवाएँ', 'Draft my pitch with AI')}
            applyLabel={T('पिच में लगाएँ', 'Use as pitch')}
            onApply={(t) => setPitch(t.slice(0, 2000))}
            input={{
              brand: o.brandName,
              campaign: o.title,
              brief: o.brief.slice(0, 1200),
              deliverables: arr(o.deliverableKinds),
              platforms: arr(o.platforms),
            }}
          />
          <div>
            <span className="field-label">
              {T('अपने काम के नमूने', 'Samples of your work')}
            </span>
            {data.samples.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {data.samples.map((s) => (
                  <Chip
                    key={s.url}
                    on={picked.includes(s.url)}
                    onClick={() =>
                      setPicked((xs) =>
                        xs.includes(s.url) ? xs.filter((x) => x !== s.url) : [...xs, s.url],
                      )
                    }
                  >
                    {s.caption || s.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 30)}
                  </Chip>
                ))}
              </div>
            )}
            <textarea
              rows={2}
              className="mt-2"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder={T('और लिंक (हर लाइन में एक)', 'More links, one per line')}
            />
          </div>
          {formError && <p className="text-sm text-bad">{formError}</p>}
          <ActionBar>
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => void submit()}
            >
              <Icon name="send" className="h-5 w-5" />
              {busy ? T('भेज रहे हैं…', 'Sending…') : T('एप्लिकेशन भेजें', 'Send application')}
            </button>
          </ActionBar>
        </Card>
      ) : (
        <Card className="text-sm text-muted">
          {!data.isPublic
            ? T(
                'अप्लाई करने के लिए अपनी मीडिया किट पब्लिक करें।',
                'Make your media kit public to apply.',
              )
            : o.acceptingApplications === false
              ? T(
                  'इस कैंपेन के आवेदन बंद हो गए।',
                  'Applications for this campaign have closed.',
                )
              : T(
                  'यह कैंपेन आपकी प्रोफ़ाइल से मेल नहीं खाता।',
                  'This campaign does not fit your profile.',
                )}
        </Card>
      )}
    </div>
  );
}
